/**
 * CAI Form Dialog - Create/Edit CAI
 * Formulario para crear o editar Constancias de Autorización de Impresión (SAR Honduras).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import { useCreateCAI, useUpdateCAI, type CAI } from "@/hooks/use-facturacion";
import { useSucursales } from "@/hooks/use-sucursales";

interface CAIFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cai: CAI | null;
  onSuccess: (isNew: boolean) => void;
}

const caiSchema = z.object({
  cai: z.string().min(1, "facturacion.cai.validation.caiRequired"),
  rango_inicio: z.string().min(1, "facturacion.cai.validation.rangoInicioRequired"),
  rango_fin: z.string().min(1, "facturacion.cai.validation.rangoFinRequired"),
  fecha_limite: z.string().min(1, "facturacion.cai.validation.fechaLimiteRequired"),
  sucursal: z.string().min(1, "facturacion.cai.validation.sucursalRequired"),
  punto_emision: z.string().min(1, "facturacion.cai.validation.puntoEmisionRequired"),
});

type FormData = z.infer<typeof caiSchema>;

export function CAIFormDialog({ open, onOpenChange, cai, onSuccess }: CAIFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);
  const { data: sucursales = [] } = useSucursales();

  const isEditing = !!cai;

  const createMutation = useCreateCAI({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdateCAI({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(caiSchema),
    defaultValues: {
      cai: "",
      rango_inicio: "",
      rango_fin: "",
      fecha_limite: "",
      sucursal: "",
      punto_emision: "",
    },
  });

  useEffect(() => {
    if (open && cai) {
      reset({
        cai: cai.cai,
        rango_inicio: cai.rango_inicio,
        rango_fin: cai.rango_fin,
        fecha_limite: cai.fecha_limite,
        sucursal: String(cai.sucursal),
        punto_emision: cai.punto_emision,
      });
      setError(null);
    } else if (open && !cai) {
      reset({
        cai: "",
        rango_inicio: "",
        rango_fin: "",
        fecha_limite: "",
        sucursal: "",
        punto_emision: "",
      });
      setError(null);
    }
  }, [open, cai, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);
    const payload = {
      cai: data.cai,
      rango_inicio: data.rango_inicio,
      rango_fin: data.rango_fin,
      fecha_limite: data.fecha_limite,
      sucursal: parseInt(data.sucursal, 10),
      punto_emision: data.punto_emision,
    };

    if (isEditing && cai) {
      updateMutation.mutate({ id: cai.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("facturacion.cai.editCAI") : t("facturacion.cai.newCAI")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("facturacion.cai.editCAIDesc") : t("facturacion.cai.newCAIDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* CAI code */}
            <div className="space-y-2">
              <Label htmlFor="cai">{t("facturacion.cai.fields.cai")} *</Label>
              <Input
                id="cai"
                placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX"
                className="placeholder:text-muted-foreground/50"
                {...register("cai")}
              />
              {errors.cai && (
                <p className="text-sm text-destructive">{t(errors.cai.message as string)}</p>
              )}
            </div>

            {/* Rango inicio / fin */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rango_inicio">{t("facturacion.cai.fields.rangoInicio")} *</Label>
                <Input
                  id="rango_inicio"
                  placeholder="000-001-01-00000001"
                  className="placeholder:text-muted-foreground/50"
                  {...register("rango_inicio")}
                />
                {errors.rango_inicio && (
                  <p className="text-sm text-destructive">
                    {t(errors.rango_inicio.message as string)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rango_fin">{t("facturacion.cai.fields.rangoFin")} *</Label>
                <Input
                  id="rango_fin"
                  placeholder="000-001-01-00000500"
                  className="placeholder:text-muted-foreground/50"
                  {...register("rango_fin")}
                />
                {errors.rango_fin && (
                  <p className="text-sm text-destructive">
                    {t(errors.rango_fin.message as string)}
                  </p>
                )}
              </div>
            </div>

            {/* Fecha limite / Punto emision */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_limite">{t("facturacion.cai.fields.fechaLimite")} *</Label>
                <Input id="fecha_limite" type="date" {...register("fecha_limite")} />
                {errors.fecha_limite && (
                  <p className="text-sm text-destructive">
                    {t(errors.fecha_limite.message as string)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="punto_emision">{t("facturacion.cai.fields.puntoEmision")} *</Label>
                <Input
                  id="punto_emision"
                  placeholder="001"
                  className="placeholder:text-muted-foreground/50"
                  {...register("punto_emision")}
                />
                {errors.punto_emision && (
                  <p className="text-sm text-destructive">
                    {t(errors.punto_emision.message as string)}
                  </p>
                )}
              </div>
            </div>

            {/* Sucursal */}
            <div className="space-y-2">
              <Label htmlFor="sucursal">{t("facturacion.cai.fields.sucursal")} *</Label>
              <Select
                value={watch("sucursal") || ""}
                onValueChange={(value) => setValue("sucursal", value, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("facturacion.cai.fields.selectSucursal")} />
                </SelectTrigger>
                <SelectContent>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sucursal && (
                <p className="text-sm text-destructive">{t(errors.sucursal.message as string)}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
